import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import React, {useState, useEffect} from 'react';
import Select, {SingleValue} from 'react-select';
import {useSelector, useDispatch} from 'react-redux';
import {AppDispatch, RootState} from '../store/store';
import {fetchSecurityGroupRules} from '../store/slices/securityRulesSlice';
import {fetchSecurityGroups} from '../store/slices/securityGroupsSlice';
import axiosInstance from '../utility/axiosInstance';
import {handleAxiosError} from '../helpers/handleAxiosError';
import {useToast} from '../components/ToastContext';
import Modal from '../components/Modal';
import { formatConsoleDate } from '../utility/formatConsoleDate';
import {useConfirm} from '../components/ConfirmContext';
import {FaPlus, FaArrowLeft, FaTrash, FaSyncAlt} from 'react-icons/fa';
import TrialLock from '../components/TrialLock';

const ITEMS_PER_PAGE = 10;

interface SecurityRule {
    id: string;
    name: string;
    description: string;
    protocol: string;
    port_range_min: string;
    port_range_max: string;
    security_group_id: string;
    created_at: string;
    updated_at: string;

    [key: string]: any;
}

const SecurityGroupsPage: React.FC = () => {
    const {confirm} = useConfirm();
    const {showToast} = useToast();
    const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
    const [showCreateRuleModal, setShowCreateRuleModal] = useState(false);
    const [showRules, setShowRules] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

    const [ruleFormData, setRuleFormData] = useState({
        direction: 'ingress',
        ethertype: 'IPv4',
        portRange: '',
        protocol: 'tcp',
    });

    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupDescription, setNewGroupDescription] = useState('');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedGroupActions, setSelectedGroupActions] = useState<{ [key: string]: string }>({});
    const [selectedRuleActions, setSelectedRuleActions] = useState<{ [key: string]: string }>({});
    const [groupsPage, setGroupsPage] = useState(0);
    const [rulesPage, setRulesPage] = useState(0);

    const dispatch = useDispatch<AppDispatch>();

    const securityGroups = useSelector((state: RootState) => state.securityGroups.data);
    const securityGroupsStatus = useSelector((state: RootState) => state.securityGroups.status);
    const securityRules = useSelector((state: RootState) => state.securityRules.data);
    const securityRulesStatus = useSelector((state: RootState) => state.securityRules.status);

    const handleManageRulesClick = (securityGroupId: string) => {
        setSelectedGroupId(securityGroupId);
        setShowRules(true);
    };

    const handleBackToGroups = () => {
        setShowRules(false);
        setSelectedGroupId(null);
    };

    const handleRuleSelectChange = (selectedOption: SingleValue<{ value: string; label: string }>) => {
        if (selectedOption) {
            setRuleFormData((prev) => ({
                ...prev,
                protocol: selectedOption.value,
            }));
        }
    };

    const handleDirectionSelectChange = (selectedOption: SingleValue<{ value: string; label: string }>) => {
        if (selectedOption) {
            setRuleFormData((prev) => ({
                ...prev,
                direction: selectedOption.value,
            }));
        }
    };

    const handleEthertypeSelectChange = (selectedOption: SingleValue<{ value: string; label: string }>) => {
        if (selectedOption) {
            setRuleFormData((prev) => ({
                ...prev,
                ethertype: selectedOption.value,
            }));
        }
    };

    const handleRuleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedGroupId || (ruleFormData.protocol !== 'icmp' && !ruleFormData.portRange)) return;

        setLoading(true);
        try {
            const port = ruleFormData.protocol === 'icmp' ? null : ruleFormData.portRange;
            const createRule = async (protocol: string) => {
                const requestBody = {
                    security_group_rule: {
                        direction: ruleFormData.direction,
                        ethertype: ruleFormData.ethertype,
                        port_range_min: port,
                        port_range_max: port,
                        protocol,
                        security_group_id: selectedGroupId,
                    },
                };
                return axiosInstance.post(`/v1/security_groups/rules/create`, requestBody);
            };

            if (ruleFormData.protocol === 'tcp,udp') {
                await Promise.all([createRule('tcp'), createRule('udp')]);
                showToast('Security rules for TCP and UDP created!', 'success');
            } else {
                await createRule(ruleFormData.protocol);
                showToast('Security rule created successfully!', 'success');
            }

            setRuleFormData({
                direction: 'ingress',
                ethertype: 'IPv4',
                portRange: '',
                protocol: 'tcp',
            });
            dispatch(fetchSecurityGroupRules());
            setShowCreateRuleModal(false);
        } catch (err) {
            console.error('Error creating rule:', err);
            const message = handleAxiosError(err);
            showToast(message || 'Failed to create rule', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateGroup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newGroupName.trim()) {
            showToast('Group name is required!', 'warning');
            return;
        }

        setLoading(true);
        try {
            await axiosInstance.post(`/v1/security_groups`, {
                SecurityGroupName: newGroupName,
                SecurityGroupDescription: newGroupDescription,
            });
            showToast('Security group created!', 'success');
            setNewGroupName('');
            setNewGroupDescription('');
            dispatch(fetchSecurityGroups());
            setShowCreateGroupModal(false);
        } catch (err) {
            console.error('Error creating group:', err);
            const message = handleAxiosError(err);
            showToast(message || 'Failed to create group', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRule = async (ruleId: string) => {
        const proceed = await confirm({
            title: 'Delete Rule',
            message: 'Are you sure you want to delete this rule?',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;

        setLoading(true);
        try {
            await axiosInstance.delete(`/v1/security_groups/rules/delete/${ruleId}`);
            dispatch(fetchSecurityGroupRules());
            showToast('Rule deleted', 'success');
        } catch (err) {
            console.error('Error deleting rule:', err);
            const message = handleAxiosError(err);
            showToast(message || 'Failed to delete rule', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteGroup = async (groupId: string) => {
        const proceed = await confirm({
            title: 'Delete Security Group',
            message: 'Are you sure you want to delete this security group?',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;

        setLoading(true);
        try {
            await axiosInstance.delete(`/v1/security_groups/${groupId}`);
            dispatch(fetchSecurityGroups());
            showToast('Security group deleted', 'success');
        } catch (err) {
            console.error('Error deleting group:', err);
            const message = handleAxiosError(err);
            showToast(message || 'Failed to delete group', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleGroupAction = (action: string, groupId: string) => {
        if (action === 'manage-rules') {
            handleManageRulesClick(groupId);
        } else if (action === 'delete') {
            handleDeleteGroup(groupId);
        }
    };

    const handleRuleAction = (action: string, ruleId: string) => {
        if (action === 'delete') {
            handleDeleteRule(ruleId);
        }
    };

    const handleGroupSelectChange = (groupId: string, action: string) => {
        setSelectedGroupActions((prev) => ({...prev, [groupId]: action}));
    };

    const handleRuleActionSelectChange = (ruleId: string, action: string) => {
        setSelectedRuleActions((prev) => ({...prev, [ruleId]: action}));
    };

    const handleRuleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setRuleFormData((prev) => ({...prev, [name]: value}));
    };

    const filteredSecurityRules = selectedGroupId
        ? securityRules.filter((rule) => rule.security_group_id === selectedGroupId)
        : [];

    useEffect(() => {
        setGroupsPage(0);
    }, [securityGroups.length]);

    useEffect(() => {
        setRulesPage(0);
    }, [filteredSecurityRules.length]);

    const ruleCountByGroup = securityRules.reduce<Record<string, number>>((acc, rule) => {
        acc[rule.security_group_id] = (acc[rule.security_group_id] || 0) + 1;
        return acc;
    }, {});

    const isPublicIngressRule = (rule: SecurityRule) => {
        const source = String(rule.remote_ip_prefix || '').trim();
        return (
            String(rule.direction || '').toLowerCase() === 'ingress' &&
            !rule.remote_group_id &&
            (!source || source === '0.0.0.0/0' || source === '::/0')
        );
    };

    const publicIngressCountByGroup = securityRules.reduce<Record<string, number>>((acc, rule) => {
        if (isPublicIngressRule(rule)) {
            acc[rule.security_group_id] = (acc[rule.security_group_id] || 0) + 1;
        }
        return acc;
    }, {});

    const getRuleSource = (rule: SecurityRule) => {
        if (rule.remote_group_id) return 'Another security group';
        return rule.remote_ip_prefix || 'Any IP';
    };

    const totalGroups = securityGroups.length;
    const totalGroupPages = Math.ceil(totalGroups / ITEMS_PER_PAGE);
    const paginatedGroups = securityGroups.slice(
        groupsPage * ITEMS_PER_PAGE,
        (groupsPage + 1) * ITEMS_PER_PAGE
    );

    const totalRules = filteredSecurityRules.length;
    const totalRulePages = Math.ceil(totalRules / ITEMS_PER_PAGE);
    const paginatedRules = filteredSecurityRules.slice(
        rulesPage * ITEMS_PER_PAGE,
        (rulesPage + 1) * ITEMS_PER_PAGE
    );

    const handleNullValue = (value: any) => (value === null ? 'Any' : value);

    const selectStyles = getSelectStyles();

    const protocolOptions = [
        {value: 'tcp', label: 'TCP'},
        {value: 'udp', label: 'UDP'},
        {value: 'tcp,udp', label: 'TCP & UDP'},
        {value: 'icmp', label: 'All ICMP'},
    ];

    const directionOptions = [
        {value: 'ingress', label: 'Ingress'},
        {value: 'egress', label: 'Egress'},
    ];

    const ethertypeOptions = [
        {value: 'IPv4', label: 'IPv4'},
        {value: 'IPv6', label: 'IPv6'},
    ];

    const renderEmptySecurityGroupState = () => (
        <div className="resource-empty-state">
            <strong>No security groups yet</strong>
            <span>Create a group, then add rules to control which traffic can reach your resources.</span>
            <TrialLock locked={false}>
                <button type="button" onClick={() => setShowCreateGroupModal(true)}>
                    Create group
                </button>
            </TrialLock>
        </div>
    );

    const renderEmptyRuleState = () => (
        <div className="resource-empty-state">
            <strong>No rules yet</strong>
            <span>Add an inbound or outbound rule to define this group&apos;s allowed traffic.</span>
            <button type="button" onClick={() => setShowCreateRuleModal(true)}>
                Create rule
            </button>
        </div>
    );

    return (
        <div className="keypairs-page">
            <div className="page-header">
                <p className="page-subtitle">
                    {showRules
                        ? 'Manage inbound and outbound traffic rules for your security group'
                        : 'Create and manage security groups to control network access'}
                </p>
                <a href="https://docs.redu.cloud/Guides/security-groups-guide#2-add-rules-to-the-group" target="_blank" rel="noopener noreferrer" className="docs-link">
                    Documentation →
                </a>
            </div>

            {error && <div className="error-banner"><p>{error}</p></div>}


            {showRules && (
                <button className="back-button" onClick={handleBackToGroups}>
                    <FaArrowLeft/> Back to Groups
                </button>
            )}


            {!showRules && (
                <>
                    <div className="desktop-view">

                        <div className="table-container">
                            <table className="keypairs-table">
                                <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Description</th>
                                    <th>Rules</th>
                                    <th>Exposure</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {securityGroupsStatus === 'loading' ? (
                                    <tr>
                                        <td colSpan={6} className="loading-state">
                                            <FaSyncAlt className="spin"/> Loading security groups...
                                        </td>
                                    </tr>
                                ) : paginatedGroups.length > 0 ? (
                                    paginatedGroups.map((group) => (
                                        <tr key={group.id}>
                                            <td className="keypair-name">{group.name}</td>
                                            <td>{group.description || '-'}</td>
                                            <td>{ruleCountByGroup[group.id] ?? 0}</td>
                                            <td>
                                                {(publicIngressCountByGroup[group.id] ?? 0) > 0 ? (
                                                    <span className="security-exposure security-exposure--public">
                                                        {publicIngressCountByGroup[group.id]} public ingress {publicIngressCountByGroup[group.id] === 1 ? 'rule' : 'rules'}
                                                    </span>
                                                ) : (
                                                    <span className="security-exposure">No public ingress</span>
                                                )}
                                            </td>
                                            <td>
                                                {group.created_at
                                                    ? formatConsoleDate(group.created_at)
                                                    : '-'}
                                            </td>
                                            <td>
                                                <Select
                                                    options={[
                                                        {value: 'manage-rules', label: 'Manage Rules'},
                                                        {value: 'delete', label: 'Delete Group'}
                                                    ]}
                                                    onChange={(option) => {
                                                        if (option) {
                                                            handleGroupAction(option.value, group.id);
                                                        }
                                                    }}
                                                    placeholder="Actions"
                                                    isSearchable={false}
                                                    menuPortalTarget={document.body}
                                                    styles={getActionSelectStyles()}
                                                />
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="empty-state">
                                            {renderEmptySecurityGroupState()}
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                            {totalGroupPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                                    <button
                                        onClick={() => setGroupsPage(p => Math.max(0, p - 1))}
                                        disabled={groupsPage === 0}
                                        style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: groupsPage === 0 ? 'not-allowed' : 'pointer', opacity: groupsPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                    >← Prev</button>
                                    <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                        {groupsPage * ITEMS_PER_PAGE + 1}-{Math.min((groupsPage + 1) * ITEMS_PER_PAGE, totalGroups)} of {totalGroups}
                                    </span>
                                    <button
                                        onClick={() => setGroupsPage(p => Math.min(totalGroupPages - 1, p + 1))}
                                        disabled={groupsPage >= totalGroupPages - 1}
                                        style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: groupsPage >= totalGroupPages - 1 ? 'not-allowed' : 'pointer', opacity: groupsPage >= totalGroupPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                    >Next →</button>
                                </div>
                            )}
                        </div>

                    </div>

                    <div className="mobile-view">
                        {securityGroupsStatus === 'loading' ? (
                            <div className="loading-state">
                                <p>Loading...</p>
                            </div>
                        ) : (
                            <div className="cards-container">
                                {paginatedGroups.length > 0 ? (
                                    paginatedGroups.map((group) => (
                                        <div key={group.id} className="keypair-card">
                                            <div className="card-header">
                                                <h3 className="card-title">{group.name}</h3>
                                                <div style={{width: '100%', marginTop: '12px'}}>
                                                    <Select
                                                        options={[
                                                            {value: 'manage-rules', label: 'Manage Rules'},
                                                            {value: 'delete', label: 'Delete Group'}
                                                        ]}
                                                        onChange={(option) => {
                                                            if (option) {
                                                                handleGroupAction(option.value, group.id);
                                                            }
                                                        }}
                                                        placeholder="Actions"
                                                        isSearchable={false}
                                                        menuPortalTarget={document.body}
                                                        styles={selectStyles}
                                            menuPosition="fixed"
                                                    />
                                                </div>
                                            </div>
                                            <div className="card-content">
                                                <div className="fingerprint">
                                                    <span className="label">Description:</span>
                                                    <span className="value">{group.description || '-'}</span>
                                                </div>
                                                <div className="fingerprint">
                                                    <span className="label">Rules:</span>
                                                    <span className="value">{ruleCountByGroup[group.id] ?? 0}</span>
                                                </div>
                                                <div className="fingerprint">
                                                    <span className="label">Exposure:</span>
                                                    <span className="value">
                                                        {(publicIngressCountByGroup[group.id] ?? 0) > 0
                                                            ? `${publicIngressCountByGroup[group.id]} public ingress ${publicIngressCountByGroup[group.id] === 1 ? 'rule' : 'rules'}`
                                                            : 'No public ingress'}
                                                    </span>
                                                </div>
                                                <div className="fingerprint">
                                                    <span className="label">Created:</span>
                                                    <span className="value">
                                    {group.created_at
                                        ? formatConsoleDate(group.created_at)
                                        : '-'}
                                </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="empty-state">
                                        {renderEmptySecurityGroupState()}
                                    </div>
                                )}
                                {totalGroupPages > 1 && (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px' }}>
                                        <button
                                            onClick={() => setGroupsPage(p => Math.max(0, p - 1))}
                                            disabled={groupsPage === 0}
                                            style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: groupsPage === 0 ? 'not-allowed' : 'pointer', opacity: groupsPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                        >← Prev</button>
                                        <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                            {groupsPage * ITEMS_PER_PAGE + 1}-{Math.min((groupsPage + 1) * ITEMS_PER_PAGE, totalGroups)} of {totalGroups}
                                        </span>
                                        <button
                                            onClick={() => setGroupsPage(p => Math.min(totalGroupPages - 1, p + 1))}
                                            disabled={groupsPage >= totalGroupPages - 1}
                                            style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: groupsPage >= totalGroupPages - 1 ? 'not-allowed' : 'pointer', opacity: groupsPage >= totalGroupPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                        >Next →</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <TrialLock locked={false}>
                    <button
                        className="create-button"
                        onClick={() => setShowCreateGroupModal(true)}
                        title="Create Security Group"
                    >
                        <FaPlus/>
                        <span>Create Group</span>
                    </button>
                    </TrialLock>
                </>
            )}


            {showRules && (
                <>
                    <div className="desktop-view">
                        {securityRulesStatus === 'loading' ? (
                            <div className="loading-state">
                                <p>Loading rules...</p>
                            </div>
                        ) : (
                            <div className="table-container">
                                <table className="keypairs-table">
                                    <thead>
                                    <tr>
                                        <th>Direction</th>
                                        <th>Ether Type</th>
                                        <th>Protocol</th>
                                        <th>Port Range</th>
                                        <th>Source</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {paginatedRules.length > 0 ? (
                                        paginatedRules.map((rule) => (
                                            <tr key={rule.id}>
                                                <td>{handleNullValue(rule.direction)}</td>
                                                <td>{handleNullValue(rule.ethertype)}</td>
                                                <td>{handleNullValue(rule.protocol)}</td>
                                                <td>
                                                    {handleNullValue(rule.port_range_min)}-
                                                    {handleNullValue(rule.port_range_max)}
                                                </td>
                                                <td>
                                                    <span className={isPublicIngressRule(rule) ? 'security-exposure security-exposure--public' : undefined}>
                                                        {getRuleSource(rule)}{isPublicIngressRule(rule) ? ' (public)' : ''}
                                                    </span>
                                                </td>
                                                <td>
                                                    {rule.created_at
                                                        ? formatConsoleDate(rule.created_at)
                                                        : '-'}
                                                </td>
                                                <td>
                                                    <Select
                                                        options={[
                                                            {value: 'delete', label: 'Delete Rule'}
                                                        ]}
                                                        onChange={(option) => {
                                                            if (option?.value === 'delete') {
                                                                handleDeleteRule(rule.id);
                                                            }
                                                        }}
                                                        placeholder="Actions"
                                                        isSearchable={false}
                                                        menuPortalTarget={document.body}
                                                        styles={selectStyles}
                                            menuPosition="fixed"
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={7} className="empty-state">
                                                {renderEmptyRuleState()}
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                                {totalRulePages > 1 && (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                                        <button
                                            onClick={() => setRulesPage(p => Math.max(0, p - 1))}
                                            disabled={rulesPage === 0}
                                            style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: rulesPage === 0 ? 'not-allowed' : 'pointer', opacity: rulesPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                        >← Prev</button>
                                        <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                            {rulesPage * ITEMS_PER_PAGE + 1}-{Math.min((rulesPage + 1) * ITEMS_PER_PAGE, totalRules)} of {totalRules}
                                        </span>
                                        <button
                                            onClick={() => setRulesPage(p => Math.min(totalRulePages - 1, p + 1))}
                                            disabled={rulesPage >= totalRulePages - 1}
                                            style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: rulesPage >= totalRulePages - 1 ? 'not-allowed' : 'pointer', opacity: rulesPage >= totalRulePages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                        >Next →</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="mobile-view">
                        {securityRulesStatus === 'loading' ? (
                            <div className="loading-state">
                                <p>Loading rules...</p>
                            </div>
                        ) : (
                            <div className="cards-container">
                                {paginatedRules.length > 0 ? (
                                    paginatedRules.map((rule) => (
                                        <div key={rule.id} className="keypair-card">
                                            <div className="card-header">
                                                <h3 className="card-title">
                                                    {handleNullValue(rule.protocol).toUpperCase()}
                                                </h3>
                                                <div style={{width: '100%', marginTop: '12px'}}>
                                                    <Select
                                                        options={[
                                                            {value: 'delete', label: 'Delete Rule'}
                                                        ]}
                                                        onChange={(option) => {
                                                            if (option?.value === 'delete') {
                                                                handleDeleteRule(rule.id);
                                                            }
                                                        }}
                                                        placeholder="Actions"
                                                        isSearchable={false}
                                                        menuPortalTarget={document.body}
                                                        styles={selectStyles}
                                            menuPosition="fixed"
                                                    />
                                                </div>
                                            </div>
                                            <div className="card-content">
                                                <div className="fingerprint">
                                                    <span className="label">Direction:</span>
                                                    <span className="value">{handleNullValue(rule.direction)}</span>
                                                </div>
                                                <div className="fingerprint">
                                                    <span className="label">Ether Type:</span>
                                                    <span className="value">{handleNullValue(rule.ethertype)}</span>
                                                </div>
                                                <div className="fingerprint">
                                                    <span className="label">Port Range:</span>
                                                    <span className="value">
                                    {handleNullValue(rule.port_range_min)}-
                                                        {handleNullValue(rule.port_range_max)}
                                </span>
                                                </div>
                                                <div className="fingerprint">
                                                    <span className="label">Source:</span>
                                                    <span className="value">
                                                        {getRuleSource(rule)}{isPublicIngressRule(rule) ? ' (public)' : ''}
                                                    </span>
                                                </div>
                                                <div className="fingerprint">
                                                    <span className="label">Created:</span>
                                                    <span className="value">
                                    {rule.created_at
                                        ? formatConsoleDate(rule.created_at)
                                        : '-'}
                                </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="empty-state">
                                        {renderEmptyRuleState()}
                                    </div>
                                )}
                                {totalRulePages > 1 && (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px' }}>
                                        <button
                                            onClick={() => setRulesPage(p => Math.max(0, p - 1))}
                                            disabled={rulesPage === 0}
                                            style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: rulesPage === 0 ? 'not-allowed' : 'pointer', opacity: rulesPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                        >← Prev</button>
                                        <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                            {rulesPage * ITEMS_PER_PAGE + 1}-{Math.min((rulesPage + 1) * ITEMS_PER_PAGE, totalRules)} of {totalRules}
                                        </span>
                                        <button
                                            onClick={() => setRulesPage(p => Math.min(totalRulePages - 1, p + 1))}
                                            disabled={rulesPage >= totalRulePages - 1}
                                            style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: rulesPage >= totalRulePages - 1 ? 'not-allowed' : 'pointer', opacity: rulesPage >= totalRulePages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                        >Next →</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <button
                        className="create-button"
                        onClick={() => setShowCreateRuleModal(true)}
                        title="Create Security Rule"
                    >
                        <FaPlus/>
                        <span>Create Rule</span>
                    </button>
                </>
            )}


            <Modal
                isOpen={showCreateGroupModal}
                onClose={() => {
                    setShowCreateGroupModal(false);
                    setNewGroupName('');
                    setNewGroupDescription('');
                }}
                title="Create Security Group"
            >
                <form onSubmit={handleCreateGroup} className="create-form">
                    <div className="form-group">
                        <label htmlFor="security-group-name">Group Name</label>
                        <input
                            id="security-group-name"
                            type="text"
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            required
                            placeholder="e.g., web-server-sg"
                            autoFocus
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="security-group-description">Description (Optional)</label>
                        <input
                            id="security-group-description"
                            type="text"
                            value={newGroupDescription}
                            onChange={(e) => setNewGroupDescription(e.target.value)}
                            placeholder="Allow HTTP, HTTPS, SSH"
                        />
                    </div>
                    <p className="help-text">
                        Learn more about security groups in our{' '}
                        <a
                            href="https://docs.redu.cloud/Guides/security-groups-guide"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            documentation
                        </a>
                        .
                    </p>
                    <div className="modal-actions">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => {
                                setShowCreateGroupModal(false);
                                setNewGroupName('');
                                setNewGroupDescription('');
                            }}
                        >
                            Cancel
                        </button>
                        <button disabled={loading} className="submit-button" type="submit">
                            {loading ? 'Creating...' : 'Create Group'}
                        </button>
                    </div>
                </form>
            </Modal>


            <Modal
                isOpen={showCreateRuleModal}
                onClose={() => {
                    setShowCreateRuleModal(false);
                    setRuleFormData({
                        direction: 'ingress',
                        ethertype: 'IPv4',
                        portRange: '',
                        protocol: 'tcp',
                    });
                }}
                title="Create Security Rule"
            >
                <form onSubmit={handleRuleSubmit} className="create-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="security-rule-ethertype">Ether Type</label>
                            <Select
                                inputId="security-rule-ethertype"
                                options={ethertypeOptions}
                                onChange={handleEthertypeSelectChange}
                                value={ethertypeOptions.find((option) => option.value === ruleFormData.ethertype)}
                                placeholder="Select ether type"
                                menuPortalTarget={document.body}
                                styles={selectStyles}
                                            menuPosition="fixed"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="security-rule-direction">Direction</label>
                            <Select
                                inputId="security-rule-direction"
                                options={directionOptions}
                                onChange={handleDirectionSelectChange}
                                value={directionOptions.find((option) => option.value === ruleFormData.direction)}
                                placeholder="Select direction"
                                menuPortalTarget={document.body}
                                styles={selectStyles}
                                            menuPosition="fixed"
                            />
                        </div>
                    </div>

                    <div className="form-group" style={{ display: ruleFormData.protocol === 'icmp' ? 'none' : 'block' }}>
                        <label htmlFor="security-rule-port">Port</label>
                        <input
                            id="security-rule-port"
                            type="number"
                            name="portRange"
                            value={ruleFormData.portRange || ''}
                            onChange={handleRuleChange}
                            required={ruleFormData.protocol !== 'icmp'}
                            placeholder="1-65535"
                            min="1"
                            max="65535"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="security-rule-protocol">Protocol</label>
                        <Select
                            inputId="security-rule-protocol"
                            options={protocolOptions}
                            onChange={handleRuleSelectChange}
                            value={protocolOptions.find((option) => option.value === ruleFormData.protocol)}
                            placeholder="Select protocol"
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                                            menuPosition="fixed"
                        />
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setShowCreateRuleModal(false)}>
                            Cancel
                        </button>
                        <button disabled={loading || (ruleFormData.protocol !== 'icmp' && !ruleFormData.portRange)} className="submit-button" type="submit">
                            {loading ? 'Creating...' : 'Create Rule'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default SecurityGroupsPage;
